<template>
  <section id="faq" class="section-padding bg-white">
    <div class="container-custom">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <div class="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-800 text-sm font-medium mb-6">
          <i class="fas fa-question-circle mr-2"></i>
          Frequently Asked Questions
        </div>
        <h2 class="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
          Got Questions?
          <span class="gradient-text">We Have Answers</span>
        </h2>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto">
          Find answers to common questions about Task Manager Pro. Can't find what you're looking for? 
          Contact our support team.
        </p>
      </div>
      
      <!-- FAQ Grid -->
      <div class="max-w-4xl mx-auto">
        <div class="space-y-6">
          <!-- FAQ Item 1 -->
          <div class="bg-gray-50 rounded-xl">
            <button 
              @click="toggleFAQ(0)"
              class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 rounded-xl transition-colors"
            >
              <span class="font-semibold text-gray-900">What is Task Manager Pro?</span>
              <i :class="['fas', 'fa-chevron-down', 'text-gray-500', 'transition-transform', { 'rotate-180': openFAQ === 0 }]"></i>
            </button>
            <div v-if="openFAQ === 0" class="px-6 pb-4">
              <p class="text-gray-700">
                Task Manager Pro is a comprehensive WordPress plugin that transforms your website into a powerful 
                project management platform. It includes Kanban boards, automated workflows, time tracking, 
                team collaboration tools, and advanced analytics.
              </p>
            </div>
          </div>
          
          <!-- FAQ Item 2 -->
          <div class="bg-gray-50 rounded-xl">
            <button 
              @click="toggleFAQ(1)"
              class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 rounded-xl transition-colors"
            >
              <span class="font-semibold text-gray-900">Do I need coding skills to use it?</span>
              <i :class="['fas', 'fa-chevron-down', 'text-gray-500', 'transition-transform', { 'rotate-180': openFAQ === 1 }]"></i>
            </button>
            <div v-if="openFAQ === 1" class="px-6 pb-4">
              <p class="text-gray-700">
                No coding skills required! Task Manager Pro is designed with a user-friendly interface that anyone 
                can use. The plugin integrates seamlessly with your WordPress admin area and provides intuitive 
                tools for managing projects and tasks.
              </p>
            </div>
          </div>
          
          <!-- FAQ Item 5 -->
          <div class="bg-gray-50 rounded-xl">
            <button 
              @click="toggleFAQ(4)"
              class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 rounded-xl transition-colors"
            >
              <span class="font-semibold text-gray-900">Is it compatible with my WordPress theme?</span>
              <i :class="['fas', 'fa-chevron-down', 'text-gray-500', 'transition-transform', { 'rotate-180': openFAQ === 4 }]"></i>
            </button>
            <div v-if="openFAQ === 4" class="px-6 pb-4">
              <p class="text-gray-700">
                Task Manager Pro is compatible with all WordPress themes and follows WordPress coding standards. 
                The plugin uses its own styling that won't interfere with your theme's design.
              </p>
            </div>
          </div>
          
          <!-- FAQ Item 6 -->
          <div class="bg-gray-50 rounded-xl">
            <button 
              @click="toggleFAQ(5)"
              class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 rounded-xl transition-colors"
            >
              <span class="font-semibold text-gray-900">Do you provide support?</span>
              <i :class="['fas', 'fa-chevron-down', 'text-gray-500', 'transition-transform', { 'rotate-180': openFAQ === 5 }]"></i>
            </button>
            <div v-if="openFAQ === 5" class="px-6 pb-4">
              <p class="text-gray-700">
                Yes! We provide comprehensive support for all our customers. Basic plan includes email support, 
                Pro plan includes priority support, and Enterprise plan includes 24/7 phone support. 
                We also have extensive documentation and video tutorials.
              </p>
            </div>
          </div>
          
          <!-- FAQ Item 7 -->
          <div class="bg-gray-50 rounded-xl">
            <button 
              @click="toggleFAQ(6)"
              class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 rounded-xl transition-colors"
            >
              <span class="font-semibold text-gray-900">Can I migrate from other project management tools?</span>
              <i :class="['fas', 'fa-chevron-down', 'text-gray-500', 'transition-transform', { 'rotate-180': openFAQ === 6 }]"></i>
            </button>
            <div v-if="openFAQ === 6" class="px-6 pb-4">
              <p class="text-gray-700">
                Yes! Task Manager Pro includes import/export functionality that allows you to migrate data from 
                other project management tools. We also provide migration assistance for Enterprise customers.
              </p>
            </div>
          </div>
          
          <!-- FAQ Item 8 -->
          <div class="bg-gray-50 rounded-xl">
            <button 
              @click="toggleFAQ(7)"
              class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 rounded-xl transition-colors"
            >
              <span class="font-semibold text-gray-900">Is my data secure?</span>
              <i :class="['fas', 'fa-chevron-down', 'text-gray-500', 'transition-transform', { 'rotate-180': openFAQ === 7 }]"></i>
            </button>
            <div v-if="openFAQ === 7" class="px-6 pb-4">
              <p class="text-gray-700">
                Absolutely! Task Manager Pro follows WordPress security best practices and includes advanced 
                permission controls. Your data stays on your server, and we never access your information. 
                We also provide regular security updates.
              </p>
            </div>
          </div>
          
          <!-- FAQ Item 9: Recent Updates -->
          <div class="bg-gray-50 rounded-xl border-2 border-green-200">
            <button 
              @click="toggleFAQ(8)"
              class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 rounded-xl transition-colors"
            >
              <span class="font-semibold text-gray-900">
                What's new in the latest update?
                <span class="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">NEW</span>
              </span>
              <i :class="['fas', 'fa-chevron-down', 'text-gray-500', 'transition-transform', { 'rotate-180': openFAQ === 8 }]"></i>
            </button>
            <div v-if="openFAQ === 8" class="px-6 pb-4">
              <p class="text-gray-700 mb-3">
                <strong>The latest update</strong> brings major performance improvements and new features:
              </p>
              <ul class="text-gray-700 space-y-2 ml-4">
                <li class="flex items-start">
                  <i class="fas fa-check text-green-500 mr-2 mt-1"></i>
                  <div>
                    <strong>80% Performance Boost:</strong> Permission caching reduces database queries by 80%, making the system lightning-fast even with large teams.
                  </div>
                </li>
                <li class="flex items-start">
                  <i class="fas fa-check text-green-500 mr-2 mt-1"></i>
                  <div>
                    <strong>Public Visibility:</strong> Share projects and tasks publicly with custom URLs and shortcodes. Perfect for client portals.
                  </div>
                </li>
                <li class="flex items-start">
                  <i class="fas fa-check text-green-500 mr-2 mt-1"></i>
                  <div>
                    <strong>Enhanced Menu Structure:</strong> Reorganized into 5 logical menus for 40% faster navigation.
                  </div>
                </li>
                <li class="flex items-start">
                  <i class="fas fa-check text-green-500 mr-2 mt-1"></i>
                  <div>
                    <strong>Batch Permission Checks:</strong> Efficient bulk operations for managing large teams.
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Contact Support -->
      <div class="mt-16 text-center">
        <div class="bg-primary-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 class="text-2xl font-bold text-gray-900 mb-4">Still Have Questions?</h3>
          <p class="text-gray-600 mb-6">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://tmp.softentra.com/wp-admin/admin.php?page=task-manager-pro" target="_blank" class="btn-primary">
              <i class="fas fa-rocket mr-2"></i>
              Live Demo
            </a>
            <a href="https://www.softentra.com/products/task-manager-pro-wordpress-project-management-plugin-with-time-tracking-team-collaboration" target="_blank" class="btn-outline">
              <i class="fas fa-shopping-cart mr-2"></i>
              Buy Now
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FAQSection',
  data() {
    return {
      openFAQ: null
    }
  },
  methods: {
    toggleFAQ(index) {
      this.openFAQ = this.openFAQ === index ? null : index
    }
  }
}
</script>
